import { declareParams, JsonApiRequestError } from '@farfetched/core';
import { zodContract } from '@farfetched/zod';
import { createStore, Store } from 'effector';
import { format } from 'patronum';
import { RegistryParams, RegistryQuery } from 'shared/lib';
import { createAuthQuery } from '../auth-http-client';
import { createPaginatedSchema, gameSettingSchema, GameSettingWithId } from '../types';

export type OriginalGameSettingRegistryParams = {
	sortField?: string | null;
	sortDirection?: string;
	page?: number;
	limit?: number;
	currencies?: string[];
	accountIds: string[];
};

export type GameSettingRegistryFilters = {
	currencies: string[];
	accountIds: number[];
};

export type GameSettingRegistryParams = RegistryParams<GameSettingRegistryFilters>;

export type GameSettingRegistryQuery = RegistryQuery<
	GameSettingWithId,
	GameSettingRegistryFilters,
	JsonApiRequestError
>;

export type CreateGameSettingRegistryQueryParams = {
	$accountId?: Store<number | null>;
	$gameId: Store<number | null>;
};

export const createGameSettingRegistryQuery = ({
	$gameId,
	$accountId,
}: CreateGameSettingRegistryQueryParams): GameSettingRegistryQuery => {
	const query = createAuthQuery({
		params: declareParams<GameSettingRegistryParams>(),
		request: {
			method: 'GET',
			url: format`/games-settings/list/${$gameId}`,
			query: {
				source: $accountId ?? createStore(null),
				fn: (params, accountId): OriginalGameSettingRegistryParams => ({
					page: params.page,
					limit: params.limit,
					sortField: params.sortField ?? undefined,
					sortDirection: params.sortDirection,
					currencies:
						params.filters.currencies?.length === 0 ? undefined : params.filters.currencies,
					accountIds: [
						...(accountId ? [String(accountId)] : []),
						...params.filters.accountIds.map(String),
					],
				}),
			},
		},
		response: {
			mapData: ({ result }) => ({
				...result,
				rows: result.rows.map<GameSettingWithId>((setting) => ({
					...setting,
					settings: setting.settings.sort((a, b) => a.code.localeCompare(b.code)),
					id: `${setting.account.id}-${setting.currency}`,
				})),
			}),
			contract: zodContract(createPaginatedSchema(gameSettingSchema)),
		},
	});

	return query;
};
